import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { delay } from "../../helper/delayresponse";

interface SmtpCampaignReport {
    id: number,
    receiver_email: string,
    status: number,
    retry_status: number,
    attempt: number,
    smtp_gateway_id: number,
    createdAt: string,
    updatedAt: string,
    smtp_gateway: {
        gateway_name: string,
        from_email: string,
    }
}

interface SmtpCampaignState {
    smtpcampaignreportdata: SmtpCampaignReport[];
    info: { count: number; current_page: number; limit: number; total_page: number } | null;
    error: string | null;
    loading: boolean;
}

interface SmtpCampaignListParams {
    id: number;
    smtp_gateway_name?: string;
    smtp_gatewayemail: string;
    sms_gateway?: number;
    gateway_id?: number;
    gateway_number?: string;
    receiver_email?: string;
    attempt?: number;
    sort_field?: string;
    sort?: 'ASC' | 'DESC';
    status?: number;
    retry_status?: number;
    limit: number;
    page: number;
}

interface SmtpCampaignResponse {
    data: SmtpCampaignReport[];
    info: {
        count: number;
        current_page: number;
        limit: number;
        total_page: number;
    };
}

export const GetSmtpCampaignReport = createAsyncThunk<
    SmtpCampaignResponse,
    SmtpCampaignListParams,
    { rejectValue: string }
>(
    'GetSmtpCampaignReport',
    async (params, { rejectWithValue }) => {
        try {
            await delay(1000)
            const response = await axiosInstance.get(`/api/admin/reports/smtp-campaign/${params.id}`, {
                params: {
                    'smtp_gateway.gateway_name': params.smtp_gateway_name,
                    'smtp_gateway.from_email': params.smtp_gatewayemail,
                    receiver_email: params.receiver_email,
                    status: params.status,
                    retry_status: params.retry_status,
                    attempt: params.attempt,
                    sort_field: params.sort_field,
                    sort: params.sort,
                    page: params.page,
                    limit: params.limit
                }
            });
            return { data: response.data.data, info: response.data.info };
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error.message);
        }
    }
);


// ==================AddSmtpCampaigns-retry-action-start===========
export const AddSmtpCampaignsReportsRetry = createAsyncThunk<
    SmtpCampaignReport,
    SmtpCampaignReport,
    { rejectValue: string }
>(
    'smtpcampaignreportdata/AddSmtpCampaignsReportsRetry',
    async (retrydata, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`api/admin/smtp-campaign/retry`, retrydata);
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error.message);
        }
    }
);
// ==================AddSmtpCampaigns-retry-action-end===========


const initialState: SmtpCampaignState = {
    smtpcampaignreportdata: [],
    info: null,
    error: null,
    loading: false,
};

const smtpcampaignreportsSlice = createSlice({
    name: "Smtpcampaignreport",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // ===============get-all-smtp-slice-start============
            .addCase(GetSmtpCampaignReport.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(GetSmtpCampaignReport.fulfilled, (state, action: PayloadAction<{ data: SmtpCampaignReport[], info: { count: number; current_page: number; limit: number; total_page: number } }>) => {
                state.loading = false;
                state.smtpcampaignreportdata = action.payload.data;
                state.info = action.payload.info;
            })
            .addCase(GetSmtpCampaignReport.rejected, (state, action: PayloadAction<string | undefined>) => {
                state.loading = false;
                state.error = action.payload || 'An error occurred';
            });
        // ===============get-all-smtp-slice-end============

        // =================add-smtpcampaign-list-retry-slice-start============
        builder
            .addCase(AddSmtpCampaignsReportsRetry.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(AddSmtpCampaignsReportsRetry.fulfilled, (state, action: PayloadAction<SmtpCampaignReport>) => {
                state.loading = false;
                state.smtpcampaignreportdata.push(action.payload);
            })
            .addCase(
                AddSmtpCampaignsReportsRetry.rejected, (state, action: PayloadAction<string | undefined>) => {
                    state.loading = false;
                    state.error = action.payload || 'Failed to add smtp campaign retry';
                }
            );
        // =================add-smtp-campaign-list-retry-slice-end============
    },
});

export default smtpcampaignreportsSlice.reducer;
